import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useSelector } from 'react-redux'

import { Polyline } from '@react-google-maps/api'
import PropTypes from 'prop-types'

import forEach from 'lodash/forEach'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import pick from 'lodash/pick'

import Drawer from '@material-ui/core/Drawer'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'

import I18n, { useT } from '@galo-monitor/i18n'
import { cog } from '@galo-monitor/icons'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import useStickyState from '@galo-monitor/services/hooks/useStickyState'
import { selectCurrentProperty } from '@galo-monitor/stores/property/selectorProperty'
import forest from '@galo-monitor/styles/assets/images/forest.svg'
import blockedHand from '@galo-monitor/styles/assets/images/hand-back-left-off.svg'
import water from '@galo-monitor/styles/assets/images/water.svg'
import colors from '@galo-monitor/styles/colors'
import { getPolygonCenter } from '@galo-monitor/utils/maps'
import Card from '@galo-monitor/web-components/Card'
import Checkbox from '@galo-monitor/web-components/Checkbox/CheckboxStyled'
import Icon from '@galo-monitor/web-components/Icon'
import { CloseIconButton } from '@galo-monitor/web-components/IconButton'
import InputSelect from '@galo-monitor/web-components/InputSelect'
import GoogleMaps, { DEFAULT_ZOOM } from '@galo-monitor/web-components/Maps/Google'
import PopupMarker from '@galo-monitor/web-components/Maps/Google/components/PopupMarker'
import PopupPolygon from '@galo-monitor/web-components/Maps/Google/components/PopupPolygon'
import withGoogleMaps from '@galo-monitor/web-components/Maps/Google/hoc/withGoogleMaps'
import Screen from '@galo-monitor/web-components/Screen'

import { polylineOptions } from './constants'
import useStyles from './styles'
import { PopupTitle, SectionTitle } from './theme'
import { buildQueryParams, checkIfMapPreferencesAreTruthy } from './utils'

const PLOT_INITIALS_BY_ID = {
  '4': 'app',
  '5': 'alr',
  '6': 'lake'
}

const PLOT_ICON_BY_ID = {
  '4': blockedHand,
  '5': forest,
  '6': water
}

const PropertyDashboardScreen = ({ isGoogleMapsLoaded }) => {
  const t = useT()
  const classes = useStyles()

  const currentProperty = useSelector(selectCurrentProperty)

  const [selectedHarvest, setSelectedHarvest] = useState('all')
  const [selectedHarvestPlots, setSelectedHarvestPlots] = useState([])
  const [mapSettingsOpen, setMapSettingsOpen] = useState(false)
  const [mapPreferences, setMapPreferences] = useStickyState({
    'property-map': false,
    'permanent-preservation-area': false,
    'legal-reserve': false,
    'dam-lake': false
  }, 'user-map-preferences')
  const [zoom, setZoom] = useState(DEFAULT_ZOOM)

  const hasCurrentProperty = !!currentProperty?.id
  const allHarvestSelected = selectedHarvest === 'all'

  const { data: persistentPlotsData } = useGaloMonitorApi(
    checkIfMapPreferencesAreTruthy(mapPreferences) && hasCurrentProperty
      ? [
        `/farms/plots/properties/list/persistent${
          buildQueryParams(
            pick(mapPreferences, [
              'permanent-preservation-area',
              'legal-reserve',
              'dam-lake'
            ])
          )
        }`,
        currentProperty.id,
        mapPreferences['permanent-preservation-area'],
        mapPreferences['legal-reserve'],
        mapPreferences['dam-lake']
      ]
      : null
  )

  const { data: harvests, mutate, isValidating } = useGaloMonitorApi(
    hasCurrentProperty && (
      allHarvestSelected
        ? '/farms/harvests/properties/list/always-date-now'
        : `/farms/harvests/properties/${ selectedHarvest }`
    )
  )

  const { data: activeHarvests } = useGaloMonitorApi(
    hasCurrentProperty
      ? ['/farms/harvests/properties/list/actives-date-now', currentProperty.id]
      : null
  )

  const {
    data: fetchedHarvestPlots,
    isValidating: isValidatingFetchedHarvestPlots
  } = useGaloMonitorApi(
    hasCurrentProperty && !allHarvestSelected
      ? `/farms/harvests/properties/${ selectedHarvest }/coordinates`
      : null
  )

  const { data: mapsData } = useGaloMonitorApi(
    mapPreferences['property-map'] && hasCurrentProperty
      ? ['/farms/properties/maps/list/all', currentProperty.id, mapPreferences['property-map']]
      : null
  )

  const markerCenter = useMemo(
    () => currentProperty ? {
      lat: +currentProperty.lat,
      lng: +currentProperty.long
    } : null,
    [currentProperty]
  )

  const propertyMaps = useMemo(
    () => mapsData ?? [],
    [mapsData]
  )

  const persistentPlots = useMemo(
    () => persistentPlotsData ?? [],
    [persistentPlotsData]
  )

  const mapCenter = useMemo(
    () => {
      if (!isEmpty(selectedHarvestPlots)) {
        let coordinates = []
        forEach(selectedHarvestPlots, plot => {
          const formattedCoordinates = map(
            plot.coordinates,
            ({ lat: latitude, long: longitude }) => ({ latitude, longitude })
          )
          coordinates = [...coordinates, ...formattedCoordinates]
        })

        return getPolygonCenter(coordinates)
      }

      if (currentProperty && currentProperty.lat && currentProperty.long)
        return {
          lat: +currentProperty.lat,
          lng: +currentProperty.long
        }

      return null
    },
    [currentProperty, selectedHarvestPlots]
  )

  const harvestOptions = useMemo(
    () =>
      isEmpty(activeHarvests)
        ? []
        : [
          {
            label: t('all harvests'),
            value: 'all'
          },
          ...map(activeHarvests, harvest => ({
            label: harvest.name,
            value: String(harvest.id)
          }))
        ],
    [activeHarvests, t]
  )

  const handleCheckMapPreferences = useCallback(
    option => {
      setMapPreferences(prevState => ({
        ...prevState,
        [option]: !prevState[option]
      }))
    },
    [setMapPreferences]
  )

  const handleCloseMapPreferencesDrawer = () => setMapSettingsOpen(false)

  useEffect(() => {
    mutate()
  }, [currentProperty, mutate])

  useEffect(() => {
    if (allHarvestSelected) {
      const plots = []

      forEach(harvests, ({ colorCulture, name: harvestName, plots: harvestPlots }) => {
        forEach(harvestPlots, plot => plots.push({
          ...plot,
          colorCulture,
          harvestName
        }))
      })

      setSelectedHarvestPlots(plots)
      return
    }

    if (!isEmpty(harvests)) {
      setSelectedHarvestPlots([
        ...map(
          fetchedHarvestPlots,
          fetchedPlot => ({
            ...fetchedPlot,
            colorCulture:  'colorCulture' in harvests ? harvests.colorCulture : harvests.color,
            harvestName: harvests.name
          })
        )
      ])
    }
  }, [fetchedHarvestPlots, harvests, allHarvestSelected, isValidating, isValidatingFetchedHarvestPlots])

  return (
    <Screen withCard>
      <GoogleMaps
        isLoaded={ isGoogleMapsLoaded }
        center={ mapCenter }
        setZoom={ setZoom }
      >
        <>
          <Card
            cardContentProps={ { style: { padding: '10px' } } }
            cardStyle={ {
              zIndex: 2,
              margin: '10px 10px 0 0',
              backgroundColor: colors.darkGrey6,
              padding: 0,
              cursor: 'pointer'
            } }
            headerStyle={ { margin: 0 } }
            style={ {
              margin: 0,
              padding: 0
            } }
            onClick={ () => setMapSettingsOpen(true) }
          >
            <Icon
              color={ colors.white }
              icon={ cog }
            />
          </Card>
          {markerCenter ? (
            <PopupMarker position={ markerCenter }>
              <I18n>your farm location is here</I18n>
            </PopupMarker>
          ) : null}
          {map(selectedHarvestPlots, harvestPlot => {
            const { coordinates } = harvestPlot
            const paths = map(coordinates, coord => ({ lat: +coord.lat, lng: +coord.long }))

            return (
              <PopupPolygon
                key={ harvestPlot.id }
                paths={ paths }
                options={ {
                  fillOpacity: 0.2,
                  strokeOpacity: 0.8,
                  strokeWeight: 2,
                  fillColor: harvestPlot.colorCulture,
                  strokeColor: harvestPlot.colorCulture
                } }
              >
                <article>
                  <div>
                    <PopupTitle>{ t('plot', { howMany: 1 }) }:</PopupTitle>{' '}
                    { harvestPlot.name }
                  </div>
                  <div>
                    <PopupTitle>{ t('harvest', { howMany: 1 }) }:</PopupTitle>{' '}
                    {harvestPlot.harvestName}
                  </div>
                </article>
              </PopupPolygon>
            )
          })}
          {map(propertyMaps, propertyMap => {
            const { coordinates } = propertyMap
            const paths = map(coordinates, coord => ({ lat: +coord.lat, lng: +coord.long }))
            paths.push(paths[0])

            return (
              <Polyline
                key={ propertyMap.id }
                path={ paths }
                options={ polylineOptions }
              />
            )
          })}
          {map(persistentPlots, plot => {
            const { coordinates } = plot
            const paths = map(coordinates, coord => ({ lat: +coord.lat, lng: +coord.long }))

            return (
              <PopupPolygon
                key={ plot.id }
                paths={ paths }
                options={ {
                  fillOpacity: 0.2,
                  strokeOpacity: 0.8,
                  strokeWeight: 2,
                  fillColor: colors.plots[PLOT_INITIALS_BY_ID[plot.plotType.id]],
                  strokeColor: colors.plots[PLOT_INITIALS_BY_ID[plot.plotType.id]]
                } }
                markerIconSvg={ PLOT_ICON_BY_ID[plot.plotType.id] }
                currentMapZoom={ zoom }
              >
                <article>
                  <div>
                    <PopupTitle>{ t('plot', { howMany: 1 }) }:</PopupTitle>{' '}
                    { plot.name }
                  </div>
                </article>
              </PopupPolygon>
            )
          })}
        </>
      </GoogleMaps>
      <Drawer
        anchor="right"
        classes={ { paper: classes.drawer } }
        open={ mapSettingsOpen }
        onClose={ handleCloseMapPreferencesDrawer }
        PaperProps={ { style: { width: 'max(20vw, 25rem)' } } }
      >
        <header className={ classes.drawerHeader }>
          <h2>{t('preference', { howMany: 2 })}</h2>
          <div className={ classes.drawerCloseBtn }>
            <CloseIconButton
              size="small"
              marginHorizontal
              marginVertical
              onClick={ handleCloseMapPreferencesDrawer }
              style={ { marginLeft: 'auto' } }
            />
          </div>
        </header>
        <InputSelect
          label={ t('harvest', { howMany: 1 }) }
          options={ harvestOptions }
          onChange={ setSelectedHarvest }
          value={ selectedHarvest ?? '' }
          disabled={ isValidating }
          styleOptions={ {
            style: {
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: '1',
              WebkitBoxOrient: 'vertical',
              textOverflow: 'ellipsis'
            }
          } }
          disableClearable
          detached
        />
        <SectionTitle>{t('show on map')}</SectionTitle>
        <List className={ classes.list } dense>
          {Object.keys(mapPreferences).map(option => (
            <ListItem className={ classes.listItem } key={ option }>
              <Checkbox
                id={ option }
                label={ t('map preferences options', { option }) }
                onChange={ () => handleCheckMapPreferences(option) }
                color="primary"
                size="medium"
                checked={ mapPreferences[option] }
              />
            </ListItem>
          ))}
        </List>
      </Drawer>
    </Screen>
  )
}

PropertyDashboardScreen.propTypes = {
  isGoogleMapsLoaded: PropTypes.bool
}

PropertyDashboardScreen.defaultProps = {
  isGoogleMapsLoaded: false
}

export default withGoogleMaps(PropertyDashboardScreen)
